import {Component, Object3D} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {StatNames} from '../enums/StatNames.js';
import {TransactionalNames} from '../enums/TransactionalNames.js';
import {GameModeType} from '../enums/GameModeType.js';
import {Area} from './level-select.js';
// import MissionData from './store-data/MissionManager.json' assert {type: 'json'};
import {Puzzle} from './puzzle.js';
import {UnlockManager} from './unlock-manager.js';
import {Statistics} from './statistics.js';
import {Localization} from '../utils/Localization.js';
import {formatNumber, timeSinceEpoch} from '../utils/Utils.js';
import {RNG} from '../utils/rng.js';
import {formatString} from '../ui/utils/utils.js';
import {GameFlowController} from './game-flow-controller.js';
import {Analytics, AnalyticsEvent} from '../classes/Analytics.js';
import {ImplementationManager} from '../classes/ImplementationManager.js';
import {DataManager} from '../classes/DataManager.js';
import {MissionManagerData} from '../types/MissionManagerData.js';
import {MissionPhase} from '../enums/MissionManagerEnems.js';

export class MissionManager extends Component {
    static TypeName = 'mission-manager';
    // Singleton
    private static _instance: MissionManager;
    private _dataloader: Promise<void>;

    static get instance(): MissionManager {
        return MissionManager._instance;
    }

    private static MAX_SIMULTANIOUS_MISSIONS = 3;

    @property.float(0.2)
    percentChanceOfHard = 0.2;

    private _missionList: MissionManagerData.MissionDefinition[]; // = MissionData.missionList;
    private _statNameOverrides: MissionManagerData.StatStringOverride[]; // = MissionData.statNameOverrides;
    private _statNameLookup = new Map<StatNames, string>();
    private _missionsLoaded = false;
    private _stateDirty: boolean;
    private _shouldReloadMissions: boolean;

    private _puzzle: Puzzle;
    private _unlockManager: UnlockManager;
    private _missions: Mission[] = [];
    private _currentPhase: MissionPhase;

    init() {
        if (MissionManager._instance) {
            console.error('There can only be one instance of MissionManager Component');
        }
        MissionManager._instance = this;

        this._dataloader = DataManager.instance
            .loadData<MissionManagerData.Data>('/data/MissionManager.bin')
            .then((data) => {
                this._missionList = data.missionList;
                this._statNameOverrides = data.statNameOverrides;
            });
    }

    start() {
        this._puzzle = Puzzle.instance;
        this._unlockManager = UnlockManager.instance;
        this._dataloader.then(() => {
            for (let x = 0; x < this._statNameOverrides.length; x++) {
                this._statNameLookup.set(
                    this._statNameOverrides[x].stat,
                    this._statNameOverrides[x].overrideString
                );
            }

            this._reloadMissions();
            this._checkForCompletion();

            this._missionsLoaded = true;
        });
    }
    onActivate(): void {
        this._puzzle.registerNewGameCallback(this._newGame);
        this._puzzle.registerGameOverCallback(this._gameOver);
        Statistics.instance.registerUpdateCallback(this._notifyStatsUpdated);
        Statistics.instance.registerChangedCallback(this._notifyStatsChanged);
    }

    onDeactivate(): void {
        this._puzzle.unregisterNewGameCallback(this._newGame);
        this._puzzle.unregisterGameOverCallback(this._gameOver);
        Statistics.instance.unRegisterUpdateCallback(this._notifyStatsUpdated);
        Statistics.instance.unRegisterChangedCallback(this._notifyStatsChanged);
    }

    update(dt: number) {
        if (!this._missionsLoaded) {
            return; // wait for data to load
        }
        if (this._shouldReloadMissions) {
            this._reloadMissions();
            this._shouldReloadMissions = false;
        }

        if (this._stateDirty) {
            this._stateDirty = false;
            this._checkForCompletion();
        }
    }

    selectNewMission(currentMissions: Mission[]): Mission {
        let mission: Mission = null;
        switch (Statistics.instance.getLong(StatNames.MissionsCompleted)) {
            case 0:
                this._currentPhase = MissionPhase.Phase1;
                break;
            case 1:
                this._currentPhase = MissionPhase.Phase2;
                break;
            default:
                const rand = Math.random();
                if (
                    Statistics.instance.getLong(StatNames.GameRank) >= 5 &&
                    rand < this.percentChanceOfHard
                ) {
                    this._currentPhase = MissionPhase.AboveRank5;
                } else if (
                    Statistics.instance.getLong(StatNames.GameRank) >= 10 &&
                    rand < this.percentChanceOfHard
                ) {
                    this._currentPhase = MissionPhase.AboveRank10;
                } else if (
                    Statistics.instance.getLong(StatNames.GameRank) >= 20 &&
                    rand < this.percentChanceOfHard
                ) {
                    this._currentPhase = MissionPhase.AboveRank20;
                } else {
                    this._currentPhase = MissionPhase.Generic;
                }
                break;
        }

        const rewardIndexes = RNG.randomNonRepeatingValues(
            0,
            this._missionList.length,
            this._missionList.length
        );
        const startIndex = RNG.getUniformInt(0, rewardIndexes.length);
        let missionIndex = -1;
        for (let x = 0; x < this._missionList.length; x++) {
            const index = rewardIndexes[(startIndex + x) % this._missionList.length];
            let inUse = false;
            const definition = this._missionList[index];
            // is this mission in use already?
            for (let y = 0; y < currentMissions.length; y++) {
                if (
                    this._missionList[currentMissions[y].baseDefinitionIndex].missionType ==
                    definition.missionType
                ) {
                    inUse = true;
                    break;
                }
            }
            // don't allow if incorrect phase (for setting up specific missions)
            if (definition.missionPhase != this._currentPhase) {
                inUse = true;
            }
            if (!inUse) {
                // do we meet the mission prerequisites?
                let meetsPrerequisites = true;
                for (let z = 0; z < definition.prerequisites.length; z++) {
                    const requirement = definition.prerequisites[z];
                    if (
                        Statistics.instance.getLong(requirement.stat) <
                        requirement.minimumRequiredValue
                    ) {
                        meetsPrerequisites = false;
                        break;
                    }
                }
                for (let z = 0; z < definition.prerequisitesTrans.length; z++) {
                    const requirement = definition.prerequisitesTrans[z];
                    if (Statistics.instance.getTransactional(requirement) == 0) {
                        meetsPrerequisites = false;
                        break;
                    }
                }
                if (meetsPrerequisites) {
                    // Is the required game mode unlocked?
                    if (
                        definition.requiredGameMode == GameModeType.None ||
                        (UnlockManager.instance.isGameModeUnlocked(
                            definition.requiredGameMode
                        ) &&
                            ImplementationManager.instance.isGameModeImplemented(
                                definition.requiredGameMode
                            ))
                    ) {
                        // we found one, stop searching!
                        missionIndex = index;
                        break;
                    }
                }
            }
        }
        if (missionIndex > -1) {
            const definition = this._missionList[missionIndex];
            mission = new Mission();
            mission.baseDefinitionIndex = missionIndex;
            mission.stat =
                definition.stats[RNG.getUniformInt(0, definition.stats.length - 1)];
            mission.valueAtMissionStart = Statistics.instance.getLong(mission.stat);
            const goal = RNG.getUniformInt(
                definition.minimumGoal,
                definition.maximumGoal + 1
            );
            let roundedGoal;
            if (goal > 100000) {
                // round to closest 10000
                roundedGoal = goal - (goal % 10000);
            } else if (goal > 10000) {
                roundedGoal = goal - (goal % 1000);
            } else if (goal > 1000) {
                // round if you're over 100
                roundedGoal = goal - (goal % 100);
            } else if (goal > 100) {
                roundedGoal = goal - (goal % 25);
            } else {
                roundedGoal = goal;
            }
            mission.goal = roundedGoal;
            const area = Area.Island;
            mission.requiredArea = area;
            mission.showProgress = definition.showProgress;
            mission.completed = false;
            mission.justCompleted = false;
        }
        return mission;
    }

    notifyStatsUpdated() {
        this._notifyStatsUpdated();
    }
    notifyStatsChanged() {
        this._notifyStatsChanged();
    }

    getMissionInstructions(index: number): {
        instructions: string;
        percentage: number;
        completed: boolean;
    } {
        let instructions = '';
        let completed = false;
        let percentage = 0.0;

        if (index >= 0 && index < this._missions.length) {
            completed = this._missions[index].completed;
            const mission = this._missions[index];
            const definition = this._missionList[mission.baseDefinitionIndex];
            const format = Localization.instance.get(definition.localizeFormatString);
            instructions = formatString(
                format,
                formatNumber(mission.goal),
                this._getStatString(mission.stat),
                GameFlowController.getModeString(definition.requiredGameMode)
            );
            const progressFormat = Localization.instance.get('UnlockProgressFormat');
            const initialValue = mission.valueAtMissionStart;
            const value = Statistics.instance.getLong(mission.stat);
            if (completed) {
                completed = true;
                percentage = 1.0;
            } else {
                percentage = (value - initialValue) / mission.goal;
            }
            if (completed == false && mission.showProgress) {
                instructions +=
                    ' ' +
                    formatString(
                        progressFormat,
                        formatNumber(value - initialValue),
                        formatNumber(mission.goal)
                    );
            }
        } else {
            return null;
        }
        return {instructions, percentage, completed};
    }

    setMissionComplete(index: number) {
        this._missions[index].completed = true;
        this._missions[index].justCompleted = true;
        this._saveMission(index);
        if (this.getMissionsComplete()) {
            Statistics.instance.recordAdditive(StatNames.MissionsCompleted, 1);
            Analytics.LogEventWithValue(AnalyticsEvent.MissionCompleted, 1);
        }
    }

    getMissionsComplete(): boolean {
        let completeCount = 0;

        for (let x = 0; x < this._missions.length; x++) {
            if (this._missions[x].completed) {
                completeCount++;
            }
        }

        let comp = completeCount == this._missions.length && this._missions.length > 0;

        return comp;
    }

    getNewMissionsCompleted(): boolean {
        let result = false;
        for (let x = 0; x < this._missions.length; x++) {
            if (this._missions[x].justCompleted) {
                result = true;
            }
        }
        return result;
    }

    getMissionJustCompleted(index: number): boolean {
        return this._missions[index].justCompleted;
    }

    clearMissionJustCompleted(index: number) {
        this._missions[index].justCompleted = false;
    }

    createNewMissions() {
        this._missions.length = 0;
        Statistics.instance.record(StatNames.Mission1Valid, 0);
        Statistics.instance.record(StatNames.Mission2Valid, 0);
        Statistics.instance.record(StatNames.Mission3Valid, 0);

        for (let x = 0; x < MissionManager.MAX_SIMULTANIOUS_MISSIONS; x++) {
            const newMission = this.selectNewMission(this._missions);
            if (newMission != null) {
                this._missions.push(newMission);
                this._saveMission(x);
            } else {
                break;
            }
        }
    }

    private _checkForCompletion() {
        if (!this.getMissionsComplete()) {
            for (let index = 0; index < this._missions.length; index++) {
                const mission = this._missions[index];
                if (!mission.completed) {
                    const definition = this._missionList[mission.baseDefinitionIndex];
                    const initialValue = mission.valueAtMissionStart;
                    const value = Statistics.instance.getLong(mission.stat);
                    // have requirements been met??
                    if (value - initialValue >= mission.goal) {
                        mission.completed = true;
                        mission.justCompleted = true;
                        this._saveMission(index);
                        //tell unlock mananager to put up a notification
                        const format = Localization.instance.get(
                            'MissionNotificationFormat'
                        );
                        const instructions = formatString(format, index + 1);
                        const format2 = Localization.instance.get(
                            definition.localizeFormatString
                        );
                        const instructions2 = formatString(
                            format2,
                            formatNumber(mission.goal),
                            this._getStatString(mission.stat),
                            GameFlowController.getModeString(definition.requiredGameMode)
                        );
                        this._unlockManager.addMissionNotificationToQueue(
                            instructions,
                            instructions2
                        );
                    }
                }
            }
            if (this.getMissionsComplete()) {
                Statistics.instance.recordAdditive(StatNames.MissionsCompleted, 1);
                Analytics.LogEventWithValue(AnalyticsEvent.MissionCompleted, 1);
            }
        }
    }

    private _reloadMissions() {
        const mission1 = this._loadMission(0);
        const mission2 = this._loadMission(1);
        const mission3 = this._loadMission(2);
        this._missions.length = 0;
        if (Statistics.instance.getLong(StatNames.TutorialGamesPlayed) > 0) {
            if (mission1 == null) {
                this.createNewMissions();
            } else {
                this._missions.push(mission1);
                if (mission2 != null) {
                    this._missions.push(mission2);
                } else {
                    // something bad happened.  Let's try to make a new mission.
                    const newMission = this.selectNewMission(this._missions);
                    if (newMission != null) {
                        this._missions.push(newMission);
                        this._saveMission(1);
                    }
                }
                if (mission3 != null) {
                    this._missions.push(mission3);
                } else {
                    // something bad happened.  Let's try to make a new mission.
                    const newMission = this.selectNewMission(this._missions);
                    if (newMission != null) {
                        this._missions.push(newMission);
                        this._saveMission(2);
                    }
                }
            }
        }
    }

    private _getStatString(stat: StatNames): string {
        let statString: string;
        if (this._statNameLookup.has(stat)) {
            statString = Localization.instance.get(this._statNameLookup.get(stat));
        } else {
            statString = Localization.instance.get(Statistics.statNamesToString(stat));
        }

        return statString;
    }

    private _saveMission(index: number) {
        const mission = this._missions[index];

        let baseIndex = StatNames.Mission1BaseIndex;
        let statIndex = StatNames.Mission1StatIndex;
        let goal = StatNames.Mission1Goal;
        let startValue = StatNames.Mission1ValueAtMissionStart;
        let area = StatNames.Mission1Area;
        let completed = StatNames.Mission1Completed;
        let valid = StatNames.Mission1Valid;
        let timeStamp = StatNames.Mission1Timestamp;

        if (index == 1) {
            baseIndex = StatNames.Mission2BaseIndex;
            statIndex = StatNames.Mission2StatIndex;
            goal = StatNames.Mission2Goal;
            startValue = StatNames.Mission2ValueAtMissionStart;
            area = StatNames.Mission2Area;
            completed = StatNames.Mission2Completed;
            valid = StatNames.Mission2Valid;
            timeStamp = StatNames.Mission2Timestamp;
        } else if (index == 2) {
            baseIndex = StatNames.Mission3BaseIndex;
            statIndex = StatNames.Mission3StatIndex;
            goal = StatNames.Mission3Goal;
            startValue = StatNames.Mission3ValueAtMissionStart;
            area = StatNames.Mission3Area;
            completed = StatNames.Mission3Completed;
            valid = StatNames.Mission3Valid;
            timeStamp = StatNames.Mission3Timestamp;
        }

        const stats = Statistics.instance;

        stats.record(baseIndex, mission.baseDefinitionIndex);
        stats.record(statIndex, mission.stat);
        stats.record(goal, mission.goal);
        stats.record(startValue, mission.valueAtMissionStart);
        stats.record(area, mission.requiredArea);
        stats.record(completed, mission.completed ? 1 : 0);
        stats.record(valid, 1);
        stats.record(timeStamp, timeSinceEpoch());
    }

    private _loadMission(index: number): Mission {
        let baseIndex = StatNames.Mission1BaseIndex;
        let statIndex = StatNames.Mission1StatIndex;
        let goal = StatNames.Mission1Goal;
        let startValue = StatNames.Mission1ValueAtMissionStart;
        let area = StatNames.Mission1Area;
        let completed = StatNames.Mission1Completed;
        let valid = StatNames.Mission1Valid;
        let timeStamp = StatNames.Mission1Timestamp;
        if (index == 1) {
            baseIndex = StatNames.Mission2BaseIndex;
            statIndex = StatNames.Mission2StatIndex;
            goal = StatNames.Mission2Goal;
            startValue = StatNames.Mission2ValueAtMissionStart;
            area = StatNames.Mission2Area;
            completed = StatNames.Mission2Completed;
            valid = StatNames.Mission2Valid;
            timeStamp = StatNames.Mission2Timestamp;
        } else if (index == 2) {
            baseIndex = StatNames.Mission3BaseIndex;
            statIndex = StatNames.Mission3StatIndex;
            goal = StatNames.Mission3Goal;
            startValue = StatNames.Mission3ValueAtMissionStart;
            area = StatNames.Mission3Area;
            completed = StatNames.Mission3Completed;
            valid = StatNames.Mission3Valid;
            timeStamp = StatNames.Mission3Timestamp;
        }
        const stats = Statistics.instance;
        let mission: Mission = null;
        if (stats.getLong(valid) == 1) {
            // TK TODO: Below was in the original, maybe it's about time to remove it?
            // safety
            // TODO: REMOVE THIS LATER
            if (stats.getLong(timeStamp) == 0) {
                stats.record(timeStamp, timeSinceEpoch());
            }
            const baseIndexValue = stats.getLong(baseIndex);
            const areaIndexValue = stats.getLong(area);
            const statIndexValue = stats.getLong(statIndex);
            if (
                baseIndexValue < this._missionList.length &&
                areaIndexValue < Object.keys(Area).length / 2 &&
                statIndexValue < Object.keys(StatNames).length / 2
            ) {
                mission = new Mission();
                mission.baseDefinitionIndex = stats.getLong(baseIndex);
                mission.stat = stats.getLong(statIndex);
                mission.goal = stats.getLong(goal);
                mission.valueAtMissionStart = stats.getLong(startValue);
                mission.requiredArea = stats.getLong(area);
                mission.completed = stats.getLong(completed) == 1 ? true : false;
                mission.showProgress =
                    this._missionList[mission.baseDefinitionIndex].showProgress;
                mission.justCompleted = false;
            }
        }
        return mission;
    }

    private _newGame = () => {
        for (let x = 0; x < this._missions.length; x++) {
            const mission = this._missions[x];
            const definition = this._missionList[mission.baseDefinitionIndex];
            if (definition.mustBeCompletedInASingleGame) {
                mission.valueAtMissionStart = Statistics.instance.getLong(mission.stat);
            }
        }
    };
    private _gameOver = () => {
        this._checkForCompletion();
    };
    private _notifyStatsUpdated = () => {
        this._stateDirty = true;
        this._shouldReloadMissions = true;
    };
    private _notifyStatsChanged = () => {
        this._stateDirty = true;
    };
}

export class Mission {
    baseDefinitionIndex: number;
    stat: StatNames;
    goal: number;
    valueAtMissionStart: number;
    showProgress: boolean;
    requiredArea: Area;
    completed: boolean;
    justCompleted: boolean;
}
